import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileText, Files, Layers, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { uploadWithProgress } from "@/lib/downloadWithProgress";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

/**
 * MultiFileUploadDialog
 * Modulo "singleton imperativo": registra un setter globale che mostra il dialog
 * quando il chiamante invoca askMultiFileAction(files).
 *
 * 3 opzioni:
 *  - separate     → restituisce i file originali invariati
 *  - merge        → POST /files/process-multi?mode=merge → 1 PDF unito
 *  - convert_each → POST /files/process-multi?mode=convert_each → N PDF (uno per file)
 *
 * Checkbox "Mantieni anche i file originali" (solo per merge / convert_each):
 *  se attiva, restituisce sia i nuovi PDF sia i file originali.
 */

let resolveCb = null;
let setStateExternal = null;

export function askMultiFileAction(files) {
  const arr = Array.isArray(files) ? files : Array.from(files || []);
  if (arr.length < 2 || !setStateExternal) {
    return Promise.resolve(arr);
  }
  // Solo se almeno un file è un'immagine (PDF puri non hanno senso da "convertire in PDF")
  // ma il merge è utile anche con tutti PDF → mostriamo sempre se ≥2 file.
  return new Promise((resolve) => {
    resolveCb = resolve;
    setStateExternal({ open: true, files: arr });
  });
}

export function MultiFileUploadDialog() {
  const [state, setState] = useState({ open: false, files: [] });
  const [busy, setBusy] = useState(false);
  const [keepOriginals, setKeepOriginals] = useState(false);
  const [choice, setChoice] = useState("separate");

  useEffect(() => {
    setStateExternal = setState;
    return () => { setStateExternal = null; };
  }, []);

  useEffect(() => {
    if (state.open) {
      setChoice("separate");
      setKeepOriginals(false);
      setBusy(false);
    }
  }, [state.open]);

  const closeAndResolve = (result) => {
    if (resolveCb) {
      resolveCb(result);
      resolveCb = null;
    }
    setState({ open: false, files: [] });
  };

  const handleCancel = () => {
    // Annulla = nessun upload (lista vuota)
    closeAndResolve([]);
  };

  const handleConfirm = async () => {
    const files = state.files || [];
    if (choice === "separate") {
      closeAndResolve(files);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f, f.name));
      if (choice === "merge") {
        const res = await api.post("/files/process-multi?mode=merge", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "blob",
        });
        const mergedName = `unito_${new Date().toISOString().slice(0,10)}.pdf`;
        const merged = new File([res.data], mergedName, { type: "application/pdf" });
        const out = keepOriginals ? [merged, ...files] : [merged];
        toast.success(`File uniti in 1 PDF (${files.length} → 1)`);
        closeAndResolve(out);
      } else {
        // convert_each
        const res = await uploadWithProgress({
          label: `📎 Conversione di ${files.length} file in PDF`,
          run: (cfg) => api.post("/files/process-multi?mode=convert_each", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: cfg.onUploadProgress,
          }),
        });
        const newFiles = (res.data?.files || []).map((f) => {
          const bin = atob(f.content_base64);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
          return new File([bytes], f.filename, { type: "application/pdf" });
        });
        const out = keepOriginals ? [...newFiles, ...files] : newFiles;
        toast.success(`Convertiti ${newFiles.length} file in PDF`);
        closeAndResolve(out);
      }
    } catch (e) {
      toast.error(formatApiError(e) || "Errore elaborazione file");
      setBusy(false);
    }
  };

  const files = state.files || [];
  const allPdf = files.every((f) => (f.type === "application/pdf") || (f.name || "").toLowerCase().endsWith(".pdf"));

  return (
    <Dialog open={state.open} onOpenChange={(v) => { if (!v && !busy) handleCancel(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="multi-file-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Files className="h-5 w-5 text-blue-600" />
            Stai caricando {files.length} file
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm text-slate-600">Come vuoi gestirli?</p>

          {/* File list (compatta) */}
          <div className="text-xs text-slate-500 max-h-24 overflow-y-auto border border-slate-200 rounded-md p-2 bg-slate-50">
            {files.map((f, i) => (
              <div key={i} className="flex items-center gap-1.5 truncate" data-testid={`multi-file-item-${i}`}>
                <FileText className="h-3 w-3 shrink-0" />
                <span className="truncate">{f.name}</span>
                <span className="text-[10px] text-slate-400 shrink-0">({Math.round(f.size / 1024)} KB)</span>
              </div>
            ))}
          </div>

          {/* Opzioni */}
          <div className="space-y-2">
            <label className={`flex items-start gap-2 cursor-pointer p-2.5 rounded-md border transition ${choice === "separate" ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
              <input type="radio" name="multi-mode" value="separate" checked={choice === "separate"} onChange={() => setChoice("separate")} data-testid="multi-opt-separate" className="mt-0.5 accent-blue-600" />
              <div className="flex-1">
                <div className="text-sm font-semibold flex items-center gap-1.5"><Files className="h-3.5 w-3.5 text-slate-600" /> Tieni separati</div>
                <div className="text-xs text-slate-500">Carica ciascun file come allegato distinto (default).</div>
              </div>
            </label>

            <label className={`flex items-start gap-2 cursor-pointer p-2.5 rounded-md border transition ${choice === "merge" ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}>
              <input type="radio" name="multi-mode" value="merge" checked={choice === "merge"} onChange={() => setChoice("merge")} data-testid="multi-opt-merge" className="mt-0.5 accent-emerald-600" />
              <div className="flex-1">
                <div className="text-sm font-semibold flex items-center gap-1.5"><Layers className="h-3.5 w-3.5 text-emerald-600" /> Unisci in un unico PDF</div>
                <div className="text-xs text-slate-500">Tutti i file (immagini e PDF) diventano un singolo PDF.</div>
              </div>
            </label>

            <label className={`flex items-start gap-2 cursor-pointer p-2.5 rounded-md border transition ${choice === "convert_each" ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:border-slate-300"} ${allPdf ? "opacity-50" : ""}`}>
              <input
                type="radio"
                name="multi-mode"
                value="convert_each"
                checked={choice === "convert_each"}
                onChange={() => setChoice("convert_each")}
                disabled={allPdf}
                data-testid="multi-opt-convert-each"
                className="mt-0.5 accent-indigo-600"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold flex items-center gap-1.5"><FileText className="h-3.5 w-3.5 text-indigo-600" /> Converti ciascuno in PDF (senza unire)</div>
                <div className="text-xs text-slate-500">{allPdf ? "Non applicabile: sono già tutti PDF." : "Ogni immagine diventa un PDF separato."}</div>
              </div>
            </label>
          </div>

          {/* Mantieni originali */}
          {choice !== "separate" && (
            <label className="flex items-center gap-2 cursor-pointer text-sm pl-1" data-testid="multi-keep-originals">
              <input
                type="checkbox"
                checked={keepOriginals}
                onChange={(e) => setKeepOriginals(e.target.checked)}
                className="h-4 w-4 accent-slate-600"
              />
              <span className="text-slate-700">Mantieni anche i file originali tra gli allegati</span>
            </label>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={busy} data-testid="multi-cancel">Annulla</Button>
          <Button onClick={handleConfirm} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="multi-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {busy ? "Elaboro…" : "Conferma e carica"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
